import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Checkbox from "common/blocks/Checkbox";
import Info from "common/blocks/Info";
import TextInput from "common/blocks/TextInput";
import useModalContext from "common/providers/modal/useModalContext";
import useWhoAmIContext from "common/providers/whoAmI/useWhoAmIContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { ALPHADIGITS_REGEX } from "lib/constants";
import { PXYourDetailsFormSchema } from "lib/schemas/schemas";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { vehicleValuedModal } from "..";
import Wrapper from "../common/Wrapper";
import styles from "./ContactInfoForm.module.scss";

const cx = classNames.bind(styles);

type ContactInfoInputs = z.infer<typeof PXYourDetailsFormSchema>;

const ContactInfoForm = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const settingToken = useRef(false);
    const { register, handleSubmit, formState, setValue } = useForm<ContactInfoInputs>({
        resolver: zodResolver(PXYourDetailsFormSchema),
    });
    const { errors } = formState;
    const { useSidebar } = useModalContext();
    const { openSidebar, registerOnCloseCallback } = useSidebar();

    const { postPartExAnswers, updateFormAnswers } = usePartExchangeQuestionsContext();
    const { isLoggedIn } = useWhoAmIContext();
    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();

    const onSubmit = async (data: ContactInfoInputs) => {
        const dataClone = {
            ...data,
        };
        const answers = updateFormAnswers("PersonalDetailsModel", dataClone);
        if (!answers || !recaptchaToken) return;
        setLoading(true);
        answers.RecaptchaToken = recaptchaToken;
        await postPartExAnswers(answers);
        setLoading(false);
        openSidebar(vehicleValuedModal());
    };

    registerOnCloseCallback(() => {});

    useEffect(() => {
        if (!recaptchaToken && !settingToken.current) {
            settingToken.current = true;
            window.grecaptcha.enterprise.ready(async () => {
                const recaptchaToken = await window.grecaptcha.enterprise.execute(
                    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
                    {
                        action: "homepage",
                    }
                );

                setRecaptchaToken(recaptchaToken);
            });
        }
    }, [recaptchaToken]);

    return (
        <Wrapper>
            <form className={cx("contactInfo")} onSubmit={handleSubmit(onSubmit)}>
                <p>How can we get in touch with you?</p>
                <TextInput
                    {...register("firstName")}
                    label="First name*"
                    type="text"
                    hasError={!!errors.firstName?.message}
                    errorText={errors.firstName?.message}
                />
                <TextInput
                    {...register("lastName")}
                    label="Last name*"
                    type="text"
                    hasError={!!errors.lastName?.message}
                    errorText={errors.lastName?.message}
                />
                <TextInput
                    {...register("emailAddress")}
                    type="text"
                    label="Email address*"
                    hasError={!!errors.emailAddress?.message}
                    errorText={errors.emailAddress?.message}
                />
                <TextInput
                    {...register("mobile")}
                    label="Mobile number"
                    type="tel"
                    hasError={!!errors.mobile?.message}
                    errorText={errors.mobile?.message}
                    inputMode="numeric"
                />
                <TextInput
                    {...register("postCode", {
                        onChange: (event) => {
                            setValue("postCode", event.target.value.toUpperCase());
                        },
                    })}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                        handleKeyDownEvent(event, ALPHADIGITS_REGEX)
                    }
                    onPaste={(event: ClipboardEvent<HTMLInputElement>) =>
                        handlePasteEvent(event, ALPHADIGITS_REGEX)
                    }
                    type="text"
                    maxLength={10}
                    label="Postcode*"
                    hasError={!!errors.postCode?.message}
                    errorText={errors.postCode?.message}
                />
                <input type="hidden" {...register("userIsLoggedIn")} />

                <Info className={cx("borderBottom")}>
                    Motorpoint may contact you about your valuation and vehicle.
                </Info>
                <div className={cx("checkbox")}>
                    <Checkbox
                        label="Tick if you would like to receive the latest news and offers from Motorpoint"
                        {...register("marketingSignUp")}
                    />
                </div>
                <input name="recaptchaToken" type="hidden" value={recaptchaToken} />
                <Button
                    className={cx("button")}
                    theme="primary"
                    type="submit"
                    fullWidth
                    ariaLabel="Value my vehicle"
                    isDisabled={!recaptchaToken}
                    loading={loading}
                >
                    Value my vehicle
                </Button>
            </form>
        </Wrapper>
    );
};

export default ContactInfoForm;
